// src/utils/loadStaff.js
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";

const titleCase = (s = "") =>
  s.toLowerCase().split(" ").filter(Boolean)
    .map(w => w[0].toUpperCase() + w.slice(1)).join(" ");

const norm = (raw = {}, roleById = new Map()) => {
  const roleId = raw.roleId || "";
  const roleFromId = roleId ? roleById.get(roleId) || "" : "";
  const fallbackRole =
    raw.role ||
    raw.roleName ||
    raw.assignedRole ||
    raw.defaultRole ||
    raw.position ||
    raw.jobRole ||
    "";

  return {
    id: raw.id,
    name: raw.name || raw.displayName || "",
    roleId,
    // roleId is canonical because Settings > Staff displays the active role
    // from roleId even when an old renamed role string still exists on the staff doc.
    role: titleCase(String(roleFromId || fallbackRole || "")),
    pin: raw.pin ?? raw.PIN ?? "",
    storeId: raw.storeId || "",
    orgId: raw.orgId || "",
    // keep archive metadata so downstream filters can work
    archived: raw.archived === true,
    isArchived: raw.isArchived === true,
    archivedAt: raw.archivedAt || null,
  };
};

function cacheKey(orgId, storeId) {
  return `kiosk.staff.${orgId}.${storeId}`;
}

function isArchivedDoc(d) {
  // Support both legacy and newer fields.
  // IMPORTANT: do NOT treat archivedAt as archived. archivedAt can remain for history after restore.
  return d?.archived === true || d?.isArchived === true;
}

export function invalidateStaffCache(orgId, storeId) {
  try { localStorage.removeItem(cacheKey(orgId, storeId)); } catch {}
}

/**
 * Canonical loader:
 * ONLY reads: /orgs/{orgId}/cashup_users where storeId == storeId
 * No root fallbacks. No "read all and filter in JS".
 */
export async function loadStaffForKiosk(inputOrgId, inputStoreId, inputStoreName = "") {
  const orgId = inputOrgId || localStorage.getItem("orgId") || "";
  const storeId = inputStoreId || localStorage.getItem("storeId") || "";
  const storeName = inputStoreName || localStorage.getItem("storeName") || localStorage.getItem("store") || "";
  if (!orgId || !storeId) return [];

  // Short cache so restore/archive reflects quickly without forcing hard refresh
  try {
    const raw = localStorage.getItem(cacheKey(orgId, storeId));
    if (raw) {
      const { at, data } = JSON.parse(raw);
      if (Date.now() - at < 20 * 1000 && Array.isArray(data)) {
        return data;
      }
    }
  } catch {}

  const colRef = collection(db, "orgs", orgId, "cashup_users");

  // Resolve the staff member's active role from roleId first.
  // This prevents renamed roles from leaving a stale role string on the kiosk.
  const roleSnap = await getDocs(collection(db, "orgs", orgId, "cashup_roles"));
  const roleById = new Map(
    roleSnap.docs
      .map((d) => ({ id: d.id, ...(d.data() || {}) }))
      .filter((r) => !isArchivedDoc(r))
      .map((r) => [r.id, r.name || r.label || ""])
  );

  // Some older records may only have store/storeName instead of storeId.
  const byId = new Map();

  const q1 = query(colRef, where("storeId", "==", storeId));
  const snap1 = await getDocs(q1);
  snap1.docs.forEach((d) => byId.set(d.id, norm({ id: d.id, ...d.data() }, roleById)));

  const sn = String(storeName || "").trim();
  if (sn) {
    const q2 = query(colRef, where("store", "==", sn));
    const snap2 = await getDocs(q2);
    snap2.docs.forEach((d) => {
      const raw = { id: d.id, ...d.data() };
      const explicitStoreId = String(raw.storeId || "").trim();

      // Legacy name fallback is allowed only when the record does not have an
      // explicit storeId, or when that explicit storeId matches this kiosk.
      if (explicitStoreId && explicitStoreId !== String(storeId)) return;

      byId.set(d.id, norm(raw, roleById));
    });
  }

  const data = Array.from(byId.values())
    .filter(x => (x.name || "").trim().length > 0)
    .filter(x => !isArchivedDoc(x))
    .filter(x => !x.storeId || String(x.storeId) === String(storeId))
    .sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), undefined, {
        sensitivity: "base",
      })
    );

  try {
    localStorage.setItem(cacheKey(orgId, storeId), JSON.stringify({ at: Date.now(), data }));
  } catch {}

  return data;
}
